/**
 * System Imports
*/

import { ChatMessage, ChatAddEventHandler } from '@system/Chat';
import { EmoteId, EmoteURL } from '@system/Emote';

/**
 * Locals
*/

let _container: HTMLDivElement;
let _resizeDebounce: NodeJS.Timeout;
let _scrollOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'end',
    inline: 'nearest',
};

const htmlregex = /[<>]/g;
const htmlmap: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;',
};

/**
 * Private Functions
*/

/**
 * @return {void}
 */
function _handleMessage(
    message: ChatMessage): void
{
    ChatBoxAddMessage(message);
}

/**
 * @param {Event} event
 *
 * @return {string}
 */
function _handleWindowResize(
    event: Event): void
{
    if (_resizeDebounce) {
        clearTimeout(_resizeDebounce);
    }

    _resizeDebounce = setTimeout(() => {
        _resizeDebounce = null;
        if (_container.children.length) {
            _container.children[_container.children.length-1].scrollIntoView(_scrollOptions);
        }
    }, 500);
}

/**
 * @return {string}
 */
function _escape(
    char: string): string
{
    return htmlmap[char];
}

/**
 * @return {string}
 */
export function _escapeHTML(
    unescaped: string): string
{
    return unescaped.replace(htmlregex, _escape);
}

/**
 * @param {ChatMessage} message
 *
 * @return {string}
 */
function _emplaceEmotes(
    message: ChatMessage): string
{
    const contents = _escapeHTML(message.contents);

    if (!message.emotes) {
        return contents;
    }

    let a = Object.entries(message.emotes || {});
    let entries: Array<[ string, string[] ]> = [];

    for (const i of a) {
        for (const j of i[1]) {
            entries.push([ i[0], [j] ]);
        }
    }

    entries.sort((a, b) => {
        let c = a[1][0].split('-')[0];
        let d = b[1][0].split('-')[0];

        return parseInt(c) - parseInt(d);
    });

    let parts = [];
    let prevMax = 0;
    let end = entries.length;

    for (let i = 0; i < end; i++) {
        let [min, max] = entries[i][1][0].split('-').map(n => parseInt(n));

        if (min) {
        parts.push(`<span>${ contents.slice(prevMax, min).trim() }</span>`);
        }

        parts.push(`<img class="emote" src="${ EmoteURL(entries[i][0], '1') }">`);

        prevMax = max + 1;
    }

    if (end) {
        let [min, max] = entries[end - 1][1][0].split('-').map(n => parseInt(n));

        if (max === contents.length) {
            parts.push(`<img class="emote" src="${ EmoteURL(entries[end - 1][0], '1') }">`)
        } else {
            parts.push(contents.slice(max + 2));
        }
    } else {
        parts.push(contents);
    }

    return parts.join(' ');

    /* const contents: Array<string> = [];
    const words: Array<string> = message.contents.split(' ');

    const end = words.length;
    let offset = 0;

    for (let i = 0; i < end; i++) {
        const emoteid = EmoteId(words[i], offset, message.emotes);

        if (emoteid) {
            contents.push(`<img src="${ EmoteURL(emoteid, '1') }">`);
        } else {
            contents.push(words[i]);
        }

        offset += words[i].length + 1; // Add 1 to account for space after the word.
    }

    return contents.join(' '); */
}

/**
 * Public Functions
*/

/**
 * @param {ChatMessage} message
 *
 * @return {void}
 */
export function ChatBoxAddMessage(
    message: ChatMessage): void
{
    const item = document.createElement('div');
    const author = document.createElement('div');
    const contents = document.createElement('div');

    item.className = 'message';
    item.dataset.author = message.user.name.toLowerCase();

    author.className = 'author';
    author.style.color = message.user.color || 'white';
    author.innerHTML = message.user.name;

    contents.className = 'contents';
    contents.innerHTML = _emplaceEmotes(message);

    item.appendChild(author);
    item.appendChild(contents);

    _container.appendChild(item);

    item.scrollIntoView(_scrollOptions);

    setTimeout(() => {
        const { bottom } = item.getBoundingClientRect();

        if (bottom > 0) {
            item.style.marginTop = `-${ bottom }px`;
            setTimeout(() => {
                item.remove();
            }, 250);
        } else {
            item.remove();
        }
    }, 10000);
}

/**
 * @return {Promise<void>}
 */
export function ChatBoxInit(): Promise<void>
{
    return new Promise((resolve) => {
        _container = document.createElement('div');

        _container.id = 'chatbox';
        _container.className = 'chatbox';
        _container.style.width = '100vw';
        _container.style.height = '100vh';

        document.body.appendChild(_container);

        window.addEventListener('resize', _handleWindowResize);
        ChatAddEventHandler(_handleMessage);

        resolve();
    });
}
